/**
 * Normalize API envelope `details` into Ant Design–friendly field → message lists.
 * Supports legacy string items and structured `{ code, message }` items from the backend.
 */

export type FieldErrorItem = string | { code: string; message: string };

/** Convert flattened detail keys like `documents.0.url` to Ant Design `name` paths. */
export function fieldPathFromDetailKey(key: string): (string | number)[] {
  return key.split('.').map((part) => (/^\d+$/.test(part) ? Number(part) : part));
}

function isStructuredItem(x: unknown): x is { code: string; message: string } {
  if (!x || typeof x !== 'object' || Array.isArray(x)) return false;
  const o = x as { code?: unknown; message?: unknown };
  return typeof o.code === 'string' && typeof o.message === 'string';
}

function isLeafErrorArray(arr: unknown[]): boolean {
  if (arr.length === 0) return true;
  return arr.every((x) => typeof x === 'string' || isStructuredItem(x));
}

function walkDetails(
  node: unknown,
  path: string,
  acc: Record<string, FieldErrorItem[]>
): void {
  if (node == null) return;

  if (Array.isArray(node)) {
    if (node.length === 0) return;
    if (isLeafErrorArray(node)) {
      const p = path || 'non_field_errors';
      acc[p] = node as FieldErrorItem[];
      return;
    }
    node.forEach((child, i) => {
      const next = path ? `${path}.${i}` : String(i);
      walkDetails(child, next, acc);
    });
    return;
  }

  if (typeof node === 'object') {
    const o = node as Record<string, unknown>;
    if (isStructuredItem(o)) {
      const p = path || 'non_field_errors';
      if (!acc[p]) acc[p] = [];
      acc[p].push(o);
      return;
    }
    for (const [k, v] of Object.entries(o)) {
      const next = path ? `${path}.${k}` : k;
      walkDetails(v, next, acc);
    }
  }
}

/** Collect nested `details` into a flat map of keys → error items. */
export function flattenApiErrorDetails(raw: unknown): Record<string, FieldErrorItem[]> {
  const acc: Record<string, FieldErrorItem[]> = {};
  walkDetails(raw, '', acc);
  return acc;
}

function extractMessage(item: FieldErrorItem): string {
  if (typeof item === 'string') return item.trim();
  return (item.message ?? '').trim();
}

/**
 * Flatten and convert to field → display strings for Ant Design `setFields` / toasts.
 */
export function normalizeEnvelopeErrorDetails(
  raw: unknown
): Record<string, string[]> | undefined {
  const flat = flattenApiErrorDetails(raw);
  const out: Record<string, string[]> = {};
  for (const [k, items] of Object.entries(flat)) {
    const msgs = items.map(extractMessage).filter(Boolean);
    if (msgs.length) out[k] = msgs;
  }
  return Object.keys(out).length ? out : undefined;
}
